/**
 * Retention Toast
 *
 * Shows a non-blocking toast when a file-backed tab is closed, reminding users
 * that the script can be found in the Scripts panel. Suppressed after 3 occurrences.
 */

import type { Tab } from '../../workspace/core/types/workspaceTypes';
import { isQueryTab, isNotebookTab } from '../../workspace/core/types/workspaceTypes';
import { toast } from '../../../components/ui/toaster';
import { useScriptBrowserStore } from '../store/scriptBrowserStore';
import { getFileNameFromPath } from '../../workspace/query-editor/utils/queryFileUtils';

const MAX_RETENTION_HINTS = 3;

/**
 * Show a retention toast when a file-backed query or notebook tab is closed.
 * Conditions: file-backed tab, not already pinned, under 3 occurrences.
 */
export function showRetentionToast(tab: Tab): void {
  // Only for file-backed query or notebook tabs
  let filePath: string | undefined;
  if (isQueryTab(tab)) {
    filePath = tab.content.filePath;
  } else if (isNotebookTab(tab)) {
    filePath = tab.content.filePath;
  }
  if (!filePath) return;

  const store = useScriptBrowserStore.getState();

  // Check suppression: max 3 times
  if (store.scriptRetentionHintCount >= MAX_RETENTION_HINTS) return;

  // Find the script entry to check if pinned
  const script = store.scripts.find(
    (s) => s.filePath.replace(/\\/g, '/').trim() === filePath.replace(/\\/g, '/').trim()
  );

  // Check if already pinned in active workspace
  const pinnedIds = store.activeWorkspaceId
    ? store.workspaces.find((w) => w.id === store.activeWorkspaceId)
        ?.pinnedScriptIds ?? []
    : store.defaultWorkspaceState.pinnedScriptIds;

  if (script && pinnedIds.includes(script.id)) return;

  // Check if file is inside a mounted folder
  const mountedFolders = store.activeWorkspaceId
    ? store.workspaces.find((w) => w.id === store.activeWorkspaceId)
        ?.mountedFolders ?? []
    : store.defaultWorkspaceState.mountedFolders;
  const normalizedFilePath = filePath.replace(/\\/g, '/').trim();
  const isInMountedFolder = mountedFolders.some((f) =>
    normalizedFilePath.startsWith(f.directoryPath.replace(/\\/g, '/').trim())
  );
  if (isInMountedFolder) return;

  // Increment retention hint count
  useScriptBrowserStore.setState((s) => ({
    scriptRetentionHintCount: s.scriptRetentionHintCount + 1,
  }));

  const fileName = getFileNameFromPath(filePath);

  toast.default({
    title: `${fileName} added to Recent Scripts`,
    duration: 8000,
    action: {
      label: 'Pin Script',
      onClick: () => {
        if (script) {
          useScriptBrowserStore.getState().togglePin(script.id);
        }
      },
    },
  });
}
